/**
 * WorkshopsPage - User-facing workshops browse and access
 * Features: Browse tab, My Workshops tab, Detail views, Purchase flow
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  Search, ShoppingBag, Lock, Play, FileText, Music, Link as LinkIcon,
  ChevronRight, ChevronDown, User, ExternalLink, AlertCircle, Check,
  Folder, BookOpen, ArrowRight, X, Loader2, CreditCard
} from 'lucide-react';
import StripeInAppCheckout from '@/components/StripeInAppCheckout';
import { Pagination } from '@/components/Pagination';
import { SpotlightSlot } from '@/components/spotlights/SpotlightSlot';
import { useTranslatedText, TranslatableText } from '@/contexts/LanguageContext';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from '@/components/UpgradeOnWebNotice';

// Content type icons
const getContentIcon = (type) => {
  switch (type) {
    case 'video': return <Play className="w-4 h-4" />;
    case 'meditation':
    case 'audio': return <Music className="w-4 h-4" />;
    case 'blog':
    case 'document':
    case 'pdf': return <FileText className="w-4 h-4" />;
    case 'link': return <LinkIcon className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
};

// Workshop Card Component
const WorkshopCard = ({ workshop, onExplore, onEnter, isPurchased }) => {
  const practitionerNames = workshop.practitioners?.map(p => p.full_name).join(', ') || 'Unknown';
  const tTitle = useTranslatedText(workshop.title);
  const tDesc = useTranslatedText(workshop.description);
  const tCategory = useTranslatedText(workshop.category?.name);
  
  return (
    <Card 
      data-testid={`workshop-card-${workshop.id}`}
      className="hover-lift border-2 border-purple-400/30 hover:border-purple-400 flex flex-col h-full"
    >
      {/* Cover Image */}
      {workshop.image_url && (
        <div className="h-40 overflow-hidden rounded-t-lg">
          <img 
            src={workshop.image_url} 
            alt={workshop.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-2">
          {workshop.category?.name && (
            <Badge variant="outline" className="text-xs">
              {workshop.category.name}
            </Badge>
          )}
          {isPurchased && (
            <Badge className="bg-green-500 text-white text-xs">
              Purchased
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg line-clamp-2">{workshop.title}</CardTitle>
        <CardDescription className="text-sm text-purple-400 line-clamp-2">
          {workshop.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="mt-auto pt-0">
        <div className="flex items-center gap-2 text-sm text-white mb-4">
          <User className="w-4 h-4" />
          <span className="line-clamp-1">{practitionerNames}</span>
        </div>
        
        {!isPurchased && workshop.total_price && (
          workshop.sale_percent ? (
            <div className="mb-3" data-testid={`workshop-card-sale-${workshop.id}`}>
              <span className="text-lg font-bold text-emerald-700">
                ${(workshop.sale_total_price ?? workshop.total_price).toFixed(2)}
              </span>
              <span className="ml-2 text-sm text-gray-500 line-through">
                ${workshop.total_price.toFixed(2)}
              </span>
              <span className="ml-2 text-xs font-semibold text-emerald-700">
                {workshop.sale_percent}% off
              </span>
            </div>
          ) : (
            <div className="text-lg font-bold text-cyan-400 mb-3">
              ${workshop.total_price.toFixed(2)}
            </div>
          )
        )}
        
        <Button 
          onClick={() => isPurchased ? onEnter(workshop) : onExplore(workshop)}
          className={`w-full ${isPurchased ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
          data-testid={isPurchased ? `enter-workshop-${workshop.id}` : `explore-workshop-${workshop.id}`}
        >
          {isPurchased ? (
            <>Enter <ArrowRight className="w-4 h-4 ml-2" /></>
          ) : (
            <>Explore <ChevronRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// Table of Contents (Locked view for preview)
const LockedTableOfContents = ({ modules }) => {
  const [expandedModules, setExpandedModules] = useState({});
  
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };
  
  if (!modules?.length) {
    return <p className="text-gray-400 italic">No content available</p>;
  }
  
  return (
    <div className="space-y-2">
      {modules.map((module) => (
        <div key={module.id} className="border border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleModule(module.id)}
            className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-750 transition-colors"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-400" />
              <span className="font-medium text-white">{module.title}</span>
            </div>
            {expandedModules[module.id] ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedModules[module.id] && (
            <div className="p-3 bg-gray-900 space-y-2">
              {/* Direct content */}
              {module.direct_content?.map((content) => (
                <div key={content.id} className="flex items-center gap-2 text-gray-400 text-sm pl-4">
                  <Lock className="w-3 h-3" />
                  {getContentIcon(content.content_type)}
                  <span>{content.title}</span>
                </div>
              ))}
              
              {/* Folders */}
              {module.folders?.map((folder) => (
                <div key={folder.id} className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-300 text-sm pl-2">
                    <Folder className="w-4 h-4 text-yellow-500" />
                    <span>{folder.title}</span>
                  </div>
                  {folder.content?.map((content) => (
                    <div key={content.id} className="flex items-center gap-2 text-gray-400 text-sm pl-8">
                      <Lock className="w-3 h-3" />
                      {getContentIcon(content.content_type)}
                      <span>{content.title}</span>
                    </div>
                  ))}
                </div>
              ))}
              
              {!module.folders?.length && !module.direct_content?.length && (
                <p className="text-gray-500 text-sm italic pl-4">No content in this module</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Cover image that turns into the practitioner's preview clip when there is one
const WorkshopPreviewMedia = ({ workshop }) => {
  const [playing, setPlaying] = useState(false);
  const url = workshop.preview_video_url;

  if (!workshop.image_url && !url) return null;

  const embedUrl = () => {
    if (url.includes('youtube.com/watch')) {
      const id = new URL(url).searchParams.get('v');
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
    }
    if (url.includes('vimeo.com/')) {
      const id = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
    return null;
  };

  if (playing && url) {
    const embed = embedUrl();
    return (
      <div className="rounded-lg overflow-hidden aspect-video bg-black" data-testid={`workshop-preview-player-${workshop.id}`}>
        {embed ? (
          <iframe
            src={embed}
            title={`${workshop.title} preview`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video src={url} controls autoPlay className="w-full h-full" />
        )}
      </div>
    );
  }

  return (
    <div className="relative h-48 rounded-lg overflow-hidden">
      {workshop.image_url && (
        <img src={workshop.image_url} alt={workshop.title} className="w-full h-full object-cover" />
      )}
      {url && (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 flex items-center justify-center bg-black/35 hover:bg-black/45 transition-colors group"
          data-testid={`workshop-preview-play-${workshop.id}`}
        >
          <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-105 transition-transform">
            <Play className="w-7 h-7 text-gray-900 ml-1" />
          </span>
          <span className="absolute bottom-3 left-3 text-xs font-medium text-white bg-black/50 px-2 py-1 rounded-full">
            Watch the preview
          </span>
        </button>
      )}
    </div>
  );
};

// Workshop Preview/Detail Modal
const WorkshopPreviewModal = ({ workshop, open, onClose, onPurchase, eligibility, loadingEligibility }) => {
  if (!workshop) return null;
  
  const practitionerNames = workshop.practitioners?.map(p => p.full_name).join(', ') || 'Unknown';
  const canPurchase = eligibility?.eligible && !eligibility?.already_purchased;
  const blockers = eligibility?.blockers || [];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{workshop.title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cover image / preview clip */}
          <WorkshopPreviewMedia workshop={workshop} />
          
          {/* Description */}
          <div>
            <h3 className="font-semibold mb-2 text-white">Description</h3>
            <p className="text-purple-400">{workshop.description}</p>
          </div>
          
          {/* Practitioners */}
          <div>
            <h3 className="font-semibold mb-2 text-white">Created by</h3>
            <div className="flex flex-wrap gap-2">
              {workshop.practitioners?.map((practitioner) => (
                <a 
                  key={practitioner.id}
                  href={`/dashboard/resources${practitioner.resource_id ? `#resource-${practitioner.resource_id}` : ''}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-full text-sm font-medium transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = `/dashboard/resources${practitioner.resource_id ? `#resource-${practitioner.resource_id}` : ''}`;
                  }}
                >
                  <User className="w-3 h-3" />
                  {practitioner.full_name}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Table of Contents */}
          <div>
            <h3 className="font-semibold mb-2 text-white flex items-center gap-2">
              <Lock className="w-4 h-4 text-yellow-500" />
              Workshop Contents (Preview)
            </h3>
            <LockedTableOfContents modules={workshop.modules} />
          </div>
          
          {/* Eligibility Blockers */}
          {blockers.length > 0 && (
            <div className="bg-gray-800 border-2 border-amber-500 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-amber-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Requirements Not Met
              </h3>
              <ul className="space-y-2">
                {blockers.map((blocker, idx) => (
                  <li key={idx} className="text-white text-sm flex items-start gap-2">
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
                    {blocker.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Pricing */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold mb-3 text-white">Pricing</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Workshop Fee:</span>
                {workshop.sale_percent ? (
                  <span data-testid="workshop-buyer-price">
                    <span className="text-emerald-300">${(workshop.sale_price ?? 0).toFixed(2)}</span>
                    <span className="ml-2 text-gray-500 line-through">${(workshop.price || 0).toFixed(2)}</span>
                  </span>
                ) : (
                  <span className="text-white" data-testid="workshop-buyer-price">${(workshop.price || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Processing Fee:</span>
                <span className="text-white" data-testid="workshop-buyer-processing-fee">
                  ${((workshop.sale_percent ? workshop.sale_processing_fee : workshop.processing_fee) || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-700">
                <span className="font-semibold text-white">Total:</span>
                {workshop.sale_percent ? (
                  <span className="text-right" data-testid="workshop-buyer-total-price">
                    <span className="font-bold text-emerald-400 text-lg">
                      ${(workshop.sale_total_price ?? workshop.total_price ?? 0).toFixed(2)}
                    </span>
                    <span className="ml-2 text-sm text-gray-400 line-through">
                      ${(workshop.total_price || 0).toFixed(2)}
                    </span>
                    <span className="block text-xs text-emerald-300 font-semibold">
                      {workshop.sale_percent}% off — applied at checkout
                    </span>
                  </span>
                ) : (
                  <span className="font-bold text-cyan-400 text-lg" data-testid="workshop-buyer-total-price">${(workshop.total_price || 0).toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Purchase Button */}
          {loadingEligibility ? (
            <Button disabled className="w-full">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking eligibility...
            </Button>
          ) : eligibility?.already_purchased ? (
            <Button disabled className="w-full bg-green-600">
              <Check className="w-4 h-4 mr-2" />
              Already Purchased
            </Button>
          ) : canPurchase ? (
            <Button 
              onClick={onPurchase}
              className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6"
              data-testid="purchase-workshop-btn"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Purchase Workshop
            </Button>
          ) : (
            <Button disabled className="w-full bg-gray-600">
              <Lock className="w-4 h-4 mr-2" />
              Cannot Purchase - Requirements Not Met
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Payment Modal
const WorkshopPaymentModal = ({ workshop, open, onClose, onSuccess }) => {
  const [showCheckout, setShowCheckout] = useState(false);
  
  const handlePaymentSuccess = () => {
    toast.success('Workshop purchased!');
    onSuccess?.();
    onClose();
  };
  
  if (!workshop) return null;

  if (isMobileBuild()) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Purchase</DialogTitle>
          </DialogHeader>
          <UpgradeOnWebNotice
            title="Purchase this workshop on our website"
            description="Workshops are purchased securely on our website. Buy there once, then access the recordings right here in the app."
            ctaLabel="Purchase on Website"
            path="/dashboard/workshops"
            testId="workshop-purchase-web-notice"
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={() => { setShowCheckout(false); onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete Purchase</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {!showCheckout ? (
            <>
              <Card className="bg-gray-800">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-white mb-2">{workshop.title}</h3>
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-gray-400">Total:</span>
                    {workshop.sale_percent ? (
                      <span>
                        <span className="text-emerald-400">
                          ${(workshop.sale_total_price ?? workshop.total_price ?? 0).toFixed(2)}
                        </span>
                        <span className="ml-2 text-sm text-gray-500 line-through">
                          ${(workshop.total_price || 0).toFixed(2)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-cyan-400">${(workshop.total_price || 0).toFixed(2)}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Button
                onClick={() => setShowCheckout(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="workshop-purchase-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Continue to Payment
              </Button>
            </>
          ) : (
            <StripeInAppCheckout
              itemType="workshop"
              itemId={workshop.id}
              onSuccess={handlePaymentSuccess}
              onError={(msg) => toast.error(msg)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Workshop Content Viewer (for purchased workshops)
const WorkshopContentViewer = ({ workshop, open, onClose }) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});
  
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };
  
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };
  
  // Render content item based on type
  const renderContent = (content) => {
    const { content_type, file_url, content_data, title } = content;
    
    switch (content_type) {
      case 'video':
        const videoUrl = content_data?.video_url || file_url;
        if (!videoUrl) return <p className="text-gray-400">No video URL</p>;
        
        // Extract embed URL for YouTube/Vimeo
        let embedUrl = videoUrl;
        if (videoUrl.includes('youtube.com/watch')) {
          const videoId = new URL(videoUrl).searchParams.get('v');
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (videoUrl.includes('youtu.be/')) {
          const videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (videoUrl.includes('vimeo.com/')) {
          const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0];
          embedUrl = `https://player.vimeo.com/video/${videoId}`;
        }
        
        return (
          <div className="aspect-video">
            <iframe
              src={embedUrl}
              className="w-full h-full rounded-lg"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        );
        
      case 'meditation':
      case 'audio':
        const audioUrl = content_data?.audio_url || file_url;
        return (
          <div className="space-y-3">
            {audioUrl && (
              <audio controls className="w-full">
                <source src={audioUrl} />
                Your browser does not support audio playback.
              </audio>
            )}
            {content_data?.instructions && (
              <div className="bg-gray-800 p-3 rounded-lg">
                <h4 className="font-medium text-white mb-2">Instructions</h4>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{content_data.instructions}</p>
              </div>
            )}
          </div>
        );
        
      case 'blog':
      case 'document':
      case 'pdf':
      case 'exercise':
        return (
          <div className="space-y-3">
            {file_url && (
              <Button asChild className="bg-purple-600 hover:bg-purple-700">
                <a href={file_url} target="_blank" rel="noopener noreferrer">
                  <FileText className="w-4 h-4 mr-2" />
                  View/Download Document
                </a>
              </Button>
            )}
            {content_data?.summary && (
              <div className="bg-gray-800 p-3 rounded-lg">
                <h4 className="font-medium text-white mb-2">Summary</h4>
                <p className="text-gray-300 text-sm">{content_data.summary}</p>
              </div>
            )}
            {content_data?.instructions && (
              <div className="bg-gray-800 p-3 rounded-lg">
                <h4 className="font-medium text-white mb-2">Instructions</h4>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{content_data.instructions}</p>
              </div>
            )}
          </div>
        );
        
      case 'link':
        return (
          <div className="space-y-3">
            {file_url && (
              <Button asChild className="bg-cyan-600 hover:bg-cyan-700">
                <a href={file_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Link
                </a>
              </Button>
            )}
            {content_data?.description && (
              <p className="text-gray-300 text-sm">{content_data.description}</p>
            )}
          </div>
        );
        
      default:
        return <p className="text-gray-400">Unknown content type</p>;
    }
  };
  
  if (!workshop) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{workshop.title}</DialogTitle>
          <p className="text-gray-400">{workshop.description}</p>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {workshop.modules?.map((module) => (
            <div key={module.id} className="border border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-750"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  <span className="font-semibold text-white">{module.title}</span>
                </div>
                {expandedModules[module.id] ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedModules[module.id] && (
                <div className="p-4 bg-gray-900 space-y-4">
                  {module.description && (
                    <p className="text-gray-400 text-sm">{module.description}</p>
                  )}
                  
                  {/* Direct content */}
                  {module.direct_content?.map((content) => (
                    <div key={content.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {getContentIcon(content.content_type)}
                        <h4 className="font-medium text-white">{content.title}</h4>
                      </div>
                      {renderContent(content)}
                    </div>
                  ))}
                  
                  {/* Folders */}
                  {module.folders?.map((folder) => (
                    <div key={folder.id} className="border border-gray-700 rounded-lg">
                      <button
                        onClick={() => toggleFolder(folder.id)}
                        className="w-full flex items-center justify-between p-3 bg-gray-750 hover:bg-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          <Folder className="w-4 h-4 text-yellow-500" />
                          <span className="text-white">{folder.title}</span>
                        </div>
                        {expandedFolders[folder.id] ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      
                      {expandedFolders[folder.id] && (
                        <div className="p-3 space-y-3">
                          {folder.content?.map((content) => (
                            <div key={content.id} className="bg-gray-800 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                {getContentIcon(content.content_type)}
                                <h5 className="font-medium text-white text-sm">{content.title}</h5>
                              </div>
                              {renderContent(content)}
                            </div>
                          ))}
                          {!folder.content?.length && (
                            <p className="text-gray-500 text-sm italic">No content in this folder</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {!module.folders?.length && !module.direct_content?.length && (
                    <p className="text-gray-500 italic">No content in this module</p>
                  )}
                </div>
              )}
            </div>
          ))}
          
          {!workshop.modules?.length && (
            <p className="text-gray-400 text-center py-8">This workshop has no content yet.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main WorkshopsPage Component
const WorkshopsPage = ({ user }) => {
  const [activeTab, setActiveTab] = useState('browse');
  const [browseWorkshops, setBrowseWorkshops] = useState([]);
  const [purchasedWorkshops, setPurchasedWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [browsePage, setBrowsePage] = useState(1);
  const [purchasedPage, setPurchasedPage] = useState(1);
  const PER_PAGE = 10;
  
  // Modal states
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [workshopContent, setWorkshopContent] = useState(null);
  
  // Eligibility state
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch browse workshops
  const fetchBrowseWorkshops = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter && categoryFilter !== 'all') params.append('category_id', categoryFilter);
      
      if (!params.has('per_page')) params.append('per_page', '50');
      const response = await axios.get(`${API}/workshops/browse?${params}`, { headers });
      setBrowseWorkshops(response.data.workshops || response.data || []);
      setBrowsePage(1);
    } catch (error) {
      console.error('Failed to load workshops', error);
      toast.error('Failed to load workshops');
    }
  }, [searchQuery, categoryFilter]);
  
  // Fetch purchased workshops
  const fetchPurchasedWorkshops = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/purchased`, { headers });
      setPurchasedWorkshops(response.data);
    } catch (error) {
      console.error('Failed to load purchased workshops', error);
    }
  }, []);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBrowseWorkshops(),
        fetchPurchasedWorkshops(),
        fetchCategories()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);
  
  // Refetch when filters change
  useEffect(() => {
    fetchBrowseWorkshops();
  }, [searchQuery, categoryFilter, fetchBrowseWorkshops]);
  
  // Check eligibility when opening preview
  const checkEligibility = async (workshopId) => {
    setLoadingEligibility(true);
    try {
      const response = await axios.get(`${API}/workshops/${workshopId}/check-eligibility`, { headers });
      setEligibility(response.data);
    } catch (error) {
      console.error('Failed to check eligibility', error);
      setEligibility({ eligible: false, blockers: [{ message: 'Failed to check eligibility' }] });
    } finally {
      setLoadingEligibility(false);
    }
  };
  
  // Handle explore click
  const handleExplore = async (workshop) => {
    try {
      const response = await axios.get(`${API}/workshops/${workshop.id}/preview`, { headers });
      setSelectedWorkshop(response.data);
      setShowPreviewModal(true);
      checkEligibility(workshop.id);
    } catch (error) {
      toast.error('Failed to load workshop details');
    }
  };
  
  // Handle enter (purchased workshop)
  const handleEnter = async (workshop) => {
    try {
      const response = await axios.get(`${API}/workshops/${workshop.id}/content`, { headers });
      setWorkshopContent(response.data);
      setShowContentModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load workshop content');
    }
  };
  
  // Handle purchase
  const handlePurchase = () => {
    setShowPreviewModal(false);
    setShowPaymentModal(true);
  };
  
  // Handle purchase success
  const handlePurchaseSuccess = () => {
    fetchBrowseWorkshops();
    fetchPurchasedWorkshops();
    setActiveTab('my-workshops');
  };
  
  // Filter workshops that aren't already purchased for browse
  const filteredBrowseWorkshops = browseWorkshops.filter(w => !w.is_purchased);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto" data-testid="workshops-page">
      <div className="mb-8">
        <TranslatableText as="h1" className="text-4xl sm:text-5xl font-bold text-white mb-3" text="Workshops" />
        <TranslatableText as="p" className="text-lg text-gray-300" text="Explore and purchase workshops from our practitioners" />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="browse" data-testid="browse-tab">
            <Search className="w-4 h-4 mr-2" />
            Browse
          </TabsTrigger>
          <TabsTrigger value="my-workshops" data-testid="my-workshops-tab">
            <ShoppingBag className="w-4 h-4 mr-2" />
            My Workshops
            {purchasedWorkshops.length > 0 && (
              <Badge className="ml-2 bg-green-500 text-white">{purchasedWorkshops.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* Browse Tab */}
        <TabsContent value="browse" className="space-y-6">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search workshops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="workshop-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 text-white" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Workshop Grid */}
          {filteredBrowseWorkshops.length > 0 ? (
            <>
              {/* Spotlight banner above the grid */}
              <SpotlightSlot slot="workshops_banner" variant="banner" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBrowseWorkshops.slice((browsePage - 1) * PER_PAGE, browsePage * PER_PAGE).flatMap((workshop, i) => {
                  const nodes = [
                    <WorkshopCard
                      key={workshop.id}
                      workshop={workshop}
                      onExplore={handleExplore}
                      onEnter={handleEnter}
                      isPurchased={false}
                    />,
                  ];
                  if ((i + 1) % 6 === 0) {
                    nodes.push(
                      <SpotlightSlot key={`sp-ws-${i}`} slot="workshops_interleave" variant="card" pick={Math.floor(i / 6)} />
                    );
                  }
                  return nodes;
                })}
              </div>
              <Pagination
                page={browsePage}
                totalPages={Math.max(1, Math.ceil(filteredBrowseWorkshops.length / PER_PAGE))}
                onChange={setBrowsePage}
                testidPrefix="workshops-browse-pagination"
                theme="dark"
              />
            </>
          ) : (
            <Card className="border-2 border-purple-400/30 bg-gradient-to-br from-purple-900/20 to-indigo-900/20">
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-purple-500/20">
                    <Search className="w-10 h-10 text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-3">No Workshops Available</h2>
                  <p className="text-gray-300 max-w-md mx-auto">
                    {searchQuery || categoryFilter !== 'all' 
                      ? 'No workshops match your search criteria. Try adjusting your filters.'
                      : 'New workshops are being created by our practitioners. Check back soon!'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* My Workshops Tab */}
        <TabsContent value="my-workshops" className="space-y-6">
          {purchasedWorkshops.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchasedWorkshops.slice((purchasedPage - 1) * PER_PAGE, purchasedPage * PER_PAGE).map((workshop) => (
                  <WorkshopCard
                    key={workshop.id}
                    workshop={workshop}
                    onExplore={handleExplore}
                    onEnter={handleEnter}
                    isPurchased={true}
                  />
                ))}
              </div>
              <Pagination
                page={purchasedPage}
                totalPages={Math.max(1, Math.ceil(purchasedWorkshops.length / PER_PAGE))}
                onChange={setPurchasedPage}
                testidPrefix="workshops-purchased-pagination"
                theme="dark"
              />
            </>
          ) : (
            <Card className="border-2 border-gray-600">
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-gray-700">
                    <ShoppingBag className="w-10 h-10 text-gray-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-3">No Purchased Workshops</h2>
                  <p className="text-gray-300 max-w-md mx-auto mb-6">
                    You haven&apos;t purchased any workshops yet. Browse our collection to find workshops that interest you.
                  </p>
                  <Button onClick={() => setActiveTab('browse')} className="bg-purple-600 hover:bg-purple-700">
                    <Search className="w-4 h-4 mr-2" />
                    Browse Workshops
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Modals */}
      <WorkshopPreviewModal
        workshop={selectedWorkshop}
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedWorkshop(null);
          setEligibility(null);
        }}
        onPurchase={handlePurchase}
        eligibility={eligibility}
        loadingEligibility={loadingEligibility}
      />
      
      <WorkshopPaymentModal
        workshop={selectedWorkshop}
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedWorkshop(null);
        }}
        onSuccess={handlePurchaseSuccess}
      />
      
      <WorkshopContentViewer
        workshop={workshopContent}
        open={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setWorkshopContent(null);
        }}
      />
    </div>
  );
};

export default WorkshopsPage;
